import cls from "classnames";
import CircularProgress from "@mui/material/CircularProgress";
import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ComponentType,
  MouseEvent,
  ReactNode,
} from "react";
import { Link, LinkProps } from "react-router-dom";

import { omit } from "utils/object";

import styles from "./index.module.scss";

type CommonProps = {
  children?: ReactNode;
  variant?: "primary" | "secondary" | "plain" | "danger" | "danger-outline";
  size?: "small" | "medium" | "large" | "extra-large";
  loading?: boolean;
  selected?: boolean;
  disabled?: boolean;
  Icon?: ComponentType<{ className?: string; htmlColor?: string }>;
  iconColor?: string;
  className?: string;
  // e2e markers (HTMLAttributes has no data-* index signature for custom components).
  "data-cy"?: string;
  "data-testid"?: string;
};

type Props =
  | (CommonProps & { as?: "button" } & Omit<
        ButtonHTMLAttributes<HTMLButtonElement>,
        keyof CommonProps
      >)
  | (CommonProps & { as: "a"; href: string } & Omit<
        AnchorHTMLAttributes<HTMLAnchorElement>,
        keyof CommonProps | "href"
      >)
  | (CommonProps & { as: "link" } & Omit<LinkProps, keyof CommonProps>);

// Peeled per branch rather than destructured in the header: a union's rest
// element does not narrow with the discriminant.
const KIT_PROPS = [
  "as",
  "children",
  "variant",
  "size",
  "loading",
  "selected",
  "disabled",
  "Icon",
  "iconColor",
  "className",
] as const;

export default function Button(props: Props) {
  // `disabled` does nothing on an anchor, so the two link forms have to be made
  // inert by hand: no destination, out of the tab order, and announced as
  // disabled rather than simply gone.
  const inert = props.disabled || props.loading;
  const className = buttonClassName(props);
  const content = (
    <>
      {props.loading && <CircularProgress className={styles.spinner} size={20} />}
      {props.Icon && (
        <props.Icon
          className={cls(styles.icon, !props.iconColor && styles.iconInk)}
          htmlColor={props.iconColor}
        />
      )}
      {props.children}
    </>
  );

  if (props.as === "a") {
    const { href, ...rest } = omit(props, KIT_PROPS);
    return (
      <a
        {...rest}
        className={className}
        href={inert ? undefined : href}
        aria-disabled={inert || undefined}
        tabIndex={inert ? -1 : rest.tabIndex}
        onClick={inert ? undefined : rest.onClick}
      >
        {content}
      </a>
    );
  }

  if (props.as === "link") {
    const rest = omit(props, KIT_PROPS);
    return (
      <Link
        {...rest}
        className={className}
        aria-disabled={inert || undefined}
        tabIndex={inert ? -1 : rest.tabIndex}
        onClick={inert ? cancelNavigation : rest.onClick}
      >
        {content}
      </Link>
    );
  }

  return (
    // Before the spread: three call sites need type="submit".
    <button
      type="button"
      {...omit(props, KIT_PROPS)}
      className={className}
      disabled={inert}
    >
      {content}
    </button>
  );
}

function cancelNavigation(event: MouseEvent) {
  event.preventDefault();
}

function buttonClassName(props: Props) {
  const { variant = "primary", size = "medium" } = props;
  return cls(styles.button, "talaera-modern-button", props.className, {
    [styles.primary]: variant === "primary",
    [styles.secondary]: variant === "secondary",
    [styles.plainButton]: variant === "plain",
    [styles.danger]: variant === "danger",
    [styles.dangerOutline]: variant === "danger-outline",
    [styles.iconButton]: !!props.Icon,
    [styles.disabled]: props.disabled,
    [styles.selected]: props.selected,
    [styles.loading]: props.loading,
    [styles.small]: size === "small",
    [styles.medium]: size === "medium",
    [styles.large]: size === "large",
    [styles.extraLarge]: size === "extra-large",
    // A block-level flex container fills its row, which a <button> resists and
    // an <a> does not.
    [styles.inline]: props.as === "a" || props.as === "link",
  });
}
